/**
 * Phase 66 Plan 06 Task 3 — Dark mode hook (D-10).
 *
 * Returns `[enabled, setEnabled]`. The setter:
 *   1. Toggles `.dark` class on `<html>` (matches Tailwind v4 `darkMode: 'class'`)
 *   2. Mirrors via `data-theme="dark"` attribute (broader CSS selector support)
 *   3. Persists to localStorage `protos_theme` ("dark" | "light")
 *
 * Read-on-mount makes a fresh window paint correctly without a flash of
 * light theme. The actual flash-prevention is owned by index.html's
 * inline boot script (Plan 01 baseline).
 *
 * Token cascade: `tokens.css` defines all light-mode tokens under `:root`
 * and dark overrides under `:root.dark` + `[data-theme="dark"]`. JSX never
 * hardcodes hex; SnapshotDiff (Plan 04) consumes `--color-diff-{added,
 * removed,changed}` defined for both modes (C-6).
 *
 * Threat T-66-06-05 (Dark-mode token override XSS): mitigated — tokens
 * are static CSS, no API-derived CSS injection surface.
 */

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "protos_theme";

function readStored(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "dark";
  } catch {
    return false;
  }
}

export function useDarkMode(): [boolean, (next: boolean) => void] {
  const [enabled, setEnabledState] = useState<boolean>(() => readStored());

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (enabled) {
      root.classList.add("dark");
      root.dataset.theme = "dark";
    } else {
      root.classList.remove("dark");
      delete root.dataset.theme;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, enabled ? "dark" : "light");
    } catch {
      // Storage may be unavailable (private mode); class toggle still works.
    }
  }, [enabled]);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
  }, []);

  return [enabled, setEnabled];
}

export { STORAGE_KEY as THEME_STORAGE_KEY };
